import React from 'react';
import { useQuery } from 'react-query';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { Ship, BarChart3, Globe, Leaf } from 'lucide-react';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { queryKeyes } from 'shared/queryKeys';
import { useSelector } from 'react-redux';
import { RootState } from 'index';
import ErrorComponent from 'Components/ErrorComponent';
import env from 'environment_system/env_system';
import { Skeleton } from '@mui/material';

interface AssesmentCardType {
    FleetId?: number;
    fleetSize?: number;
}

const AssesmentCards: React.FC<AssesmentCardType> = ({ FleetId, fleetSize }) => {
    const { Vessels, onboardedVessels } = useSelector(
        (state: RootState) => state.Reporting
    );
    const isFleet = typeof FleetId === 'number' && FleetId > 0;
    const staticData = {
        ch4_emission_tonnes: 0.016,
        co2_emission_tonnes: 1035.95,
        eua_required_tonnes: 725.165,
        ghgie_gCO2e_per_tonne_NM: -10526858.81,
        n2o_emission_tonnes: 0.058
    }

    let { data: AssesmentCardData, isLoading, isError } = useQuery(
        [queryKeyes.fleets.AssesmentCardData.key, FleetId],
        async () =>
            isFleet
                ? await Get(queryKeyes.fleets.AssesmentCardDataFleet.url(FleetId), FleetId)
                : await Get(queryKeyes.fleets.AssesmentCardData.url()),
        {
            enabled: true,
            staleTime: 0
        }
    );

    const formatEmission = (value?: number) => {
        if (value === undefined || value === null) return '-';
        return value < 0.01 ? value.toExponential(2) : value.toFixed(2);
    };


    const cardData = [
        {
            id: 1,
            title: FleetId ? 'Fleet Size' : 'Total Vessels',
            subtitle: FleetId ? 'No. of Vessels' : undefined,
            value: FleetId ? fleetSize : undefined,
            items: !FleetId
                ? [
                    { label: 'Onboarded', value: env?.demo ? 1 : onboardedVessels?.length ?? 0 },
                    {
                        label: 'Yet to onboard',
                        value: env?.demo ? 0 : (Vessels?.length ?? 0) - (onboardedVessels?.length ?? 0)
                    }
                ]
                : undefined,
            icon: Ship,
            bgClass: 'bg-primary',
            iconColorClass: 'text-primary',
            watermarkPosition: 'bottom-right'
        },
        {
            id: 2,
            title: 'Total Emission Analysis (Estd) (2026)',
            items: [
                {
                    label: 'CO₂ Emission',
                    value: env?.demo ? `${formatEmission(staticData?.co2_emission_tonnes)} mt` : `${formatEmission(AssesmentCardData?.co2_emission_tonnes)} mt`
                },
                {
                    label: 'CH₄ Emission',
                    value: env?.demo ? `${formatEmission(staticData?.ch4_emission_tonnes)} mt` : `${formatEmission(AssesmentCardData?.ch4_emission_tonnes)} mt`
                },
                {
                    label: 'N₂O Emission',
                    value: env?.demo ? `${formatEmission(staticData?.n2o_emission_tonnes)} mt` : `${formatEmission(AssesmentCardData?.n2o_emission_tonnes)} mt`
                }
            ],
            icon: BarChart3,
            bgClass: 'bg-danger',
            iconColorClass: 'text-danger',
            watermarkPosition: 'bottom-right'
        },
        env.fuel_eu && {
            id: 3,
            title: 'Total Estimated EUA (2026)',
            value: env?.demo ? staticData?.eua_required_tonnes?.toFixed(0) : AssesmentCardData?.eua_required_tonnes?.toFixed(0),
            icon: Globe,
            bgClass: 'bg-warning',
            iconColorClass: 'text-warning',
            watermarkPosition: 'bottom-right'
        },
        env.fuel_eu && {
            id: 4,
            title: 'Fuel EU (Estd) (2026)',
            items: [
                {
                    label: 'Total Comp. Balance',
                    value: env?.demo
                        ? staticData?.ghgie_gCO2e_per_tonne_NM?.toFixed(3)
                        : AssesmentCardData?.graph_total_compliance_balance
                            ? AssesmentCardData?.graph_total_compliance_balance.toLocaleString(undefined, { maximumFractionDigits: 2 })
                            : '-'
                },
                {
                    label: 'Total Penalty (€)',
                    value: env?.demo
                        ? '0.00'
                        : AssesmentCardData?.total_penalty
                            ? `€${AssesmentCardData?.total_penalty.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                            : '-'
                }
            ],
            icon: Leaf,
            bgClass: 'bg-success',
            iconColorClass: 'text-success',
            watermarkPosition: 'bottom-right'
        }
    ].filter(Boolean);


    return (
        <Row className="mb-4">
            {isLoading &&
                Array.from({ length: 4 }).map((_, index) => (
                    <Col key={index} xl={3} lg={6} md={6} sm={12} className='px-2'>
                        <Card className="h-100 rounded-3 bg-light">
                            <CardBody className="position-relative p-3">
                                <Skeleton variant="text" width="80%" height={28} />
                                <Skeleton variant="text" width="60%" height={20} />
                                <Skeleton variant="text" width="40%" height={18} />
                                <Skeleton variant="text" width="40%" height={18} />
                                <Skeleton variant="text" width="40%" height={18} />
                            </CardBody>
                        </Card>
                    </Col>
                ))
            }
            {isError &&
                <Col lg={4}>
                    <ErrorComponent message="Unable to load required data!" />
                </Col>
            }
            {!isLoading && !isError &&
                cardData.map((card: any) => {
                    const IconComponent = card.icon;
                    return (
                        <Col key={card.id} xl={3} lg={6} md={6} sm={12} className='px-2'>
                            <Card className={`dashboard-card card-${card.bgClass.replace('bg-', '')} h-100 rounded-3 border-0`}>
                                <CardBody className="text-white card-content position-relative p-3">
                                    {/* Watermark Icon */}
                                    <IconComponent
                                        className={`watermark-icon watermark-${card.watermarkPosition} text-white`}
                                        size={card.watermarkPosition === 'center' ? 80 : 64}
                                    />

                                    {/* Title */}
                                    <h6 className="card-title-assesment text-white" style={{ fontSize: card.id === 2 ? '0.7rem' : '0.8rem' }}>{card.title} </h6>

                                    {/* Value or Content */}
                                    {card.value ? (
                                        <div>
                                            {card.subtitle && (
                                                <small className="card-subtitle text-white">{card.subtitle}</small>
                                            )}
                                            <div className={`${card?.id === 3 ? 'text-center' : ''} card-value text-white`}>{card.value}</div>
                                        </div>
                                    ) : (
                                        <div>
                                            {card.items?.map((item: any, index: number) => (
                                                <div key={index} className="emission-item text-white d-flex justify-content-between">
                                                    <span className="emission-label">{item.label}</span>
                                                    <span className="emission-value">{item.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
        </Row>
    );
};

export default AssesmentCards;
